import React, { useRef, useState } from "react";
import { ScrollView, View } from "react-native";
import { useRouter } from "expo-router";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import { courseService } from "@/services/courseService";
import Head from "expo-router/head";
import { CourseCreateForm, CourseCreateFormRef } from "./CourseCreateForm";
import type { CourseStatusType } from "@/types/db/courses";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { getSaveBehavior } from "@/lib/saveSettings";

export default function NewCoursePage() {
  const toast = useToast();
  const router = useRouter();
  const { triggerRefresh } = useRowSelectionContext();
  const formRef = useRef<CourseCreateFormRef>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [name, setName] = useState("");

  const handleCreateCourse = async () => {
    const metaData = formRef.current?.getMetaData();
    if (!metaData) return;

    if (!name.trim()) {
      toast.showToast({
        title: "Course name required",
        message: "Please enter a name for the course",
        toastType: "error",
      });
      return;
    }

    try {
      setSaving(true);
      const formData = formRef.current?.getFormData();
      const newCourse = await courseService.createCourse({
        name: name,
        description: formData?.description || undefined,
        status: formData?.status || "DRAFT",
        open_enrollment: formData?.openEnrollment || false,
        meta_data: metaData,
      });
      toast.showToast({ title: "Course created successfully", toastType: "success" });
      triggerRefresh("course");

      // Handle save behavior setting
      const behavior = await getSaveBehavior("course");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: newCourse?.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      toast.showToast({
        title: "Failed to create course",
        message: err.message,
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setName("");
    formRef.current?.resetForm();
  };

  return (
    <>
      <Head>
        <title>NEXUS New Course</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="course"
          name={name}
          onNameChange={setName}
          namePlaceholder="Course name"
          dirty={dirty}
          saving={saving}
          onSave={handleCreateCourse}
          onCancel={handleReset}
        />
        <View style={{ flex: 1, padding: 16 }}>
          <CourseCreateForm ref={formRef} onDirtyChange={setDirty} />
        </View>
      </View>
    </>
  );
}
